/**
 * Transient-error retry helpers for Supabase reads.
 *
 * Why this exists:
 *  • `makeTimeoutFetch` aborts a stalled request with `AbortSignal.timeout`.
 *    supabase-js does NOT re-throw that abort — it catches the fetch rejection
 *    and returns it as a result `error` whose `message` is
 *    "TimeoutError: The operation was aborted due to timeout" and whose `code`
 *    is empty (""). So transient detection must inspect the error *message*,
 *    not only thrown exceptions or PG error codes.
 *  • Postgres `statement_timeout` surfaces as a returned error with code 57014.
 *  • A brief DB blip / cold-start / saturation spike usually clears within a
 *    few hundred ms, so one short retry rides through it. A genuinely broken
 *    query (e.g. undefined column 42703) fails deterministically and must NOT
 *    be retried — `isTransientDbError` returns false for those.
 *
 * This mirrors the retry-once pattern already used by `getProductDetailInternal`
 * in `lib/api/products/detail.ts`, generalised for reuse.
 */

/** Minimal shape shared by thrown Errors and supabase result `error` objects. */
type ErrorLike = {
  name?: string;
  code?: string;
  message?: string;
  details?: string | null;
};

export interface RetryOptions {
  /** Number of *additional* attempts after the first (default 1). */
  retries?: number;
  /** Base backoff before the first retry, doubled each attempt (default 300ms). */
  baseDelayMs?: number;
  /** Upper bound on a single backoff (default 1000ms). */
  maxDelayMs?: number;
}

/**
 * True when an error is a transient infrastructure failure worth retrying:
 * client fetch timeout/abort, PG statement_timeout (57014), or connection drop.
 * Permanent errors (bad column, constraint violation, auth) return false.
 */
export function isTransientDbError(error: unknown): boolean {
  if (!error) return false;

  const e = error as ErrorLike;

  // Thrown DOM/undici errors (rare — supabase usually returns instead of throws).
  if (e.name === "TimeoutError" || e.name === "AbortError") return true;

  // PG statement_timeout. (Permanent codes like 42703 fall through to false.)
  if (e.code === "57014") return true;

  // makeTimeoutFetch abort surfaces here: empty code, message carries the text.
  const haystack = `${e.message ?? ""} ${e.details ?? ""}`;
  if (!haystack.trim()) return false;
  return (
    /aborted due to timeout/i.test(haystack) ||
    /operation was aborted/i.test(haystack) ||
    /\bTimeoutError\b/.test(haystack) ||
    /\bAbortError\b/.test(haystack) ||
    /connection reset|ECONNRESET|socket hang up/i.test(haystack) ||
    /fetch failed|network error|failed to fetch/i.test(haystack)
  );
}

function backoff(attempt: number, base: number, max: number): Promise<void> {
  const delay = Math.min(base * 2 ** attempt, max);
  return new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * Run an arbitrary async op, retrying only on transient errors. The op is
 * expected to THROW on failure (use for `.rpc()` paths wrapped to throw, or any
 * promise that rejects). Non-transient errors and exhausted retries re-throw.
 */
export async function withTransientRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const { retries = 1, baseDelayMs = 300, maxDelayMs = 1000 } = opts;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= retries || !isTransientDbError(error)) throw error;
      await backoff(attempt, baseDelayMs, maxDelayMs);
    }
  }
}

/**
 * Execute a supabase query builder (or `.rpc()`) with transient retry, keeping
 * the familiar `{ data, error, count }` return shape — it NEVER throws for a
 * transient error, it returns the last result so existing `if (error)` handling
 * stays intact. `build` MUST construct a fresh query each call (a PostgREST
 * builder cannot be safely re-awaited).
 *
 *   const { data, error, count } = await runQueryWithRetry(() =>
 *     supabase.from("products").select(SEL).eq("status", "approved")
 *   );
 */
export async function runQueryWithRetry<R extends { error: ErrorLike | null }>(
  build: () => PromiseLike<R>,
  opts: RetryOptions = {},
): Promise<R> {
  const { retries = 1, baseDelayMs = 300, maxDelayMs = 1000 } = opts;
  let last: R;
  for (let attempt = 0; ; attempt++) {
    try {
      last = await build();
    } catch (thrown) {
      // supabase-js rarely throws, but a low-level network failure can. Normalise
      // it into the result shape so callers see one consistent contract.
      last = { data: null, error: thrown as ErrorLike } as unknown as R;
    }
    if (!last.error || attempt >= retries || !isTransientDbError(last.error)) {
      return last;
    }
    await backoff(attempt, baseDelayMs, maxDelayMs);
  }
}
